import { RichText, Elements } from 'prismic-reactjs';
import React from 'react';
import { useQuery } from 'react-apollo';
import { useParams } from 'react-router';

import { GET_SINGLE_POST } from 'main/graphql';
import { LoadingSpinner, SEO } from 'main/components';

interface Params {
  slug?: string;
}

type Props = Params;

// List of allowed domains and paths that shouldn't get NOFOLLOW
const ALLOWED_DOMAINS = ['orqafpv.com/firmware', 'www.orqafpv.com/'];

const normalizeUrl = (url: string) => {
  try {
    const parsedUrl = new URL(url);
    let path = parsedUrl.pathname;
    if (path.endsWith('/')) {
      path = path.slice(0, -1);
    }
    return `${parsedUrl.hostname}${path}`;
  } catch {
    return url;
  }
};

// Custom serializer for links - all links will receive NOFOLLOW except websites from ALLOWED_DOMAINS
const htmlSerializer = (type: any, element: any, content: any) => {
  if (type === Elements.hyperlink) {
    const url = element.data.url;
    const normalizedUrl = normalizeUrl(url);

    // Check if the URL matches any of our allowed domains
    const isAllowed = ALLOWED_DOMAINS.some(domain => {
      const normalizedDomain = domain.endsWith('/')
        ? domain.slice(0, -1)
        : domain;
      return normalizedUrl.startsWith(normalizedDomain);
    });

    return (
      <a
        href={url}
        rel={isAllowed ? 'noopener noreferrer' : 'nofollow noopener noreferrer'}
        target="_blank"
        style={{ textDecoration: 'underline' }}
      >
        {content}
      </a>
    );
  }
  return null;
};

export const BlogDetails: React.FC<Props> = () => {
  const { slug } = useParams<Params>();
  const { data, loading, error } = useQuery(GET_SINGLE_POST, {
    variables: {
      uid: slug,
    },
  });

  return (
    <article className="blog-details">
      <div>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            <SEO
              seoTitle={`${data?.blog_post?.title?.[0]?.text} - Orqa`}
              seoDescription={`${data?.blog_post?.short_content?.[0]?.text}`}
            />
            {error && <p>Error loading post...</p>}
            {data && data.blog_post && (
              <div className="blog-details__wrapper">
                {data.blog_post.hero_image?.url && (
                  <div className="blog-details__hero">
                    {/* Responsive hero image */}
                    <div className="blog-details__hero-image-container">
                      <picture>
                        {/* Mobile: 767px and smaller */}
                        <source
                          media="(max-width: 500px)"
                          srcSet={
                            data.blog_post.mobile_image?.url ||
                            data.blog_post.hero_image.url
                          }
                        />
                        {/* Tablet Vertical: 768px - 991px */}
                        <source
                          media="(min-width: 501px) and (max-width: 991px)"
                          srcSet={
                            data.blog_post.tablet_image_vert?.url ||
                            data.blog_post.hero_image.url
                          }
                        />
                        {/* Tablet: 992px - 1200px */}
                        <source
                          media="(min-width: 992px) and (max-width: 1200px)"
                          srcSet={
                            data.blog_post.tablet_image?.url ||
                            data.blog_post.hero_image.url
                          }
                        />
                        {/* Desktop: 1201px and wider */}
                        <source
                          media="(min-width: 1201px)"
                          srcSet={
                            data.blog_post.desktop_image?.url ||
                            data.blog_post.hero_image.url
                          }
                        />
                        {/* Fallback image */}
                        <img
                          src={data.blog_post.hero_image.url}
                          alt=""
                          className="blog-details__hero-image"
                        />
                      </picture>
                    </div>

                    <div className="blog-details__hero__title">
                      <div className="wrapper wrapper--narrow">
                        <h1 className="t-beta s-bottom--med">
                          {data.blog_post.title[0]?.text}
                        </h1>
                        <div className="blog-details__meta">
                          <p className="t-tiny t-upper o-80">
                            <span className="s-right--lrg">
                              {data.blog_post.published_date}
                            </span>
                            <span>{data.blog_post.author}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="blog-details__content">
                  <div className="wrapper wrapper--narrow">
                    {data.blog_post?.short_content && (
                      <div className="formatted s-bottom--xlrg">
                        <RichText
                          render={data.blog_post?.short_content}
                          htmlSerializer={htmlSerializer}
                        />
                      </div>
                    )}

                    <div className="formatted">
                      <RichText
                        render={data.blog_post.long_content}
                        htmlSerializer={htmlSerializer}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            {!data?.blog_post && <p>Post not found...</p>}
          </>
        )}
      </div>
    </article>
  );
};
